import PropTypes from 'prop-types'
import { memo, useMemo, useRef } from 'react'
import AdaptiveImage from 'components/elements/AdaptiveImage/AdaptiveImage'
import Slideshow from 'components/elements/Slideshow/Slideshow'
import SunburstBackground from 'components/elements/SunburstBackground/SunburstBackground'
import useActiveStateAnimationChildren from 'lib/react/hooks/useActiveStateAnimationChildren'
import classNames from 'lib/util/classNames'
import styles from './ContentSlideshow.module.scss'

/**
 * The `ContentSlideshow` component
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function ContentSlideshow (props) {
  const { aspectRatio, fit, images, isActive } = props
  const ref = useRef()
  const classNameOutput = useMemo(
    () => classNames([styles.container, fit && styles.fit]),
    [fit]
  )
  const outputStyles = useMemo(
    () => ({
      '--aspect-ratio': `1 / ${aspectRatio}`
    }),
    [aspectRatio]
  )

  // Animation
  useActiveStateAnimationChildren(ref, isActive)

  return (
    <div className={classNameOutput} ref={ref} style={outputStyles}>
      <Slideshow>
        <Slideshow.Navigation>
          <Slideshow.Slides className={styles.slides}>
            {images.map((image, i) => (
              <Slideshow.Slide key={i}>
                <SunburstBackground />
                <AdaptiveImage
                  className={styles.image}
                  src={image}
                  sizes={[
                    [500, 400],
                    [500, 700],
                    [1000, 900]
                  ]}
                />
              </Slideshow.Slide>
            ))}
          </Slideshow.Slides>
        </Slideshow.Navigation>
        <Slideshow.TimedIndicators />
      </Slideshow>
    </div>
  )
}

/** @type {object} */
ContentSlideshow.propTypes = {
  aspectRatio: PropTypes.number,
  fit: PropTypes.bool,
  images: PropTypes.arrayOf(PropTypes.string),
  isActive: PropTypes.bool
}

/** @type {object} */
ContentSlideshow.defaultProps = {
  aspectRatio: 1,
  fit: false,
  images: [],
  isActive: false
}

export default memo(ContentSlideshow)
